import { useRef, useState } from "react";
import { uploadFile } from "../api/storage";
import { C, font } from "../styles/tokens";
import { useDense } from "./FormDensity";

/**
 * FileUpload component for certificates and registration documents.
 *
 * Behaviour:
 *   - Click or drag-and-drop to choose a file
 *   - On file selection, immediately uploads to S3 via presigned URL
 *   - Shows progress states: idle, uploading, done, error
 *   - On success, calls onUpload({ s3Key, fileName }) so the parent form can save
 *
 * Constraints (also enforced server-side):
 *   - 5 MB max
 *   - PDF, JPEG, PNG only
 */
export function FileUpload({
  category = "certificate",
  value,
  onUpload,
  onClear,
  disabled,
}) {
  const fileInputRef = useRef(null);
  const [state, setState] = useState("idle"); // idle | uploading | error
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const dense = useDense();
  // In dense mode the dropzone shrinks its padding so it doesn't dominate the
  // already-tight long forms.
  const zoneStyle = {
    ...zoneBaseStyle,
    padding: dense ? "9px 14px" : "20px 16px",
  };

  async function handleFile(file) {
    setState("uploading");
    setError(null);
    try {
      const result = await uploadFile(file, category);
      setState("idle");
      onUpload?.(result);
    } catch (err) {
      setState("error");
      setError(err.message ?? "Upload failed.");
    }
  }

  function onInputChange(e) {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  }

  function onDrop(e) {
    e.preventDefault();
    setDragActive(false);
    if (disabled) return;
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }

  function onDragOver(e) {
    e.preventDefault();
    if (!disabled) setDragActive(true);
  }

  function onDragLeave() {
    setDragActive(false);
  }

  // Done state - file is uploaded and saved
  if (value?.s3Key) {
    return (
      <div
        style={{
          ...zoneStyle,
          borderColor: C.green,
          background: C.greenLight,
          cursor: "default",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <CheckIcon />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: C.green,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {value.fileName || "Document uploaded"}
            </div>
            {!disabled && (
              <div style={{ fontSize: 11, color: C.textMed }}>
                Click below to replace
              </div>
            )}
          </div>
          {onClear && !disabled && (
            <button onClick={onClear} type="button" style={clearButtonStyle}>
              Remove
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div
        onClick={() => !disabled && fileInputRef.current?.click()}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        style={{
          ...zoneStyle,
          borderColor: dragActive ? C.blue : C.dividerStrong,
          background: dragActive ? C.blueLight : C.bgMuted,
          cursor: disabled ? "default" : "pointer",
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={onInputChange}
          disabled={disabled || state === "uploading"}
          style={{ display: "none" }}
        />
        <div style={{ textAlign: "center" }}>
          {state === "uploading" ? (
            <>
              <div style={{ fontSize: 13, color: C.textMed, marginBottom: 4 }}>
                Uploading...
              </div>
              <div style={{ fontSize: 11, color: C.textLight }}>
                Your file is being saved securely.
              </div>
            </>
          ) : dense ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              <CloudUpIcon size={18} />
              <span
                style={{ fontSize: 12, fontWeight: 600, color: C.textDark }}
              >
                Click to upload or drag &amp; drop
              </span>
              <span style={{ fontSize: 11, color: C.textLight }}>
                PDF, JPG or PNG, up to 5 MB
              </span>
            </div>
          ) : (
            <>
              <CloudUpIcon />
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: C.textDark,
                  marginTop: 6,
                }}
              >
                Click to upload or drag and drop
              </div>
              <div style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>
                PDF, JPG, or PNG up to 5 MB
              </div>
            </>
          )}
        </div>
      </div>
      {error && (
        <div style={{ fontSize: 12, color: C.red, marginTop: 6 }}>{error}</div>
      )}
    </div>
  );
}

const zoneBaseStyle = {
  border: "2px dashed",
  borderRadius: 6,
  fontFamily: font,
  transition: "border-color 0.15s ease, background 0.15s ease",
};

const clearButtonStyle = {
  background: "transparent",
  border: 0,
  color: C.red,
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  padding: 4,
};

function CloudUpIcon({ size = 28 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={C.textMed}
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "block", margin: "0 auto" }}
    >
      <path d="M16 16l-4-4-4 4" />
      <path d="M12 12v9" />
      <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke={C.green}
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
